import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useToast } from '@/hooks/use-toast';

export interface LQIntention {
  id?: string;
  night_number: number;
  intention: string;
  created_at?: string;
}

export interface LQDua {
  id?: string;
  dua_text: string;
  nights_made: number[];
  created_at?: string;
}

export interface LQWorship {
  id?: string;
  night_number: number;
  extra_salah: boolean;
  quran_recited: boolean;
  dhikr_done: boolean;
  charity_given: boolean;
  dua_made: boolean;
}

export interface LQReflection {
  id?: string;
  night_number: number;
  reflection: string;
  prompt?: string;
  created_at?: string;
}

const STORAGE_KEY = 'laylatulQadrData';

interface LQLocalData {
  intentions: LQIntention[];
  duas: LQDua[];
  worship: LQWorship[];
  reflections: LQReflection[];
}

const defaultLocal: LQLocalData = { intentions: [], duas: [], worship: [], reflections: [] };

export const useLaylatulQadr = () => {
  const { user, isGuest } = useAuth();
  const { toast } = useToast();
  const [intentions, setIntentions] = useState<LQIntention[]>([]);
  const [duas, setDuas] = useState<LQDua[]>([]);
  const [worship, setWorship] = useState<LQWorship[]>([]);
  const [reflections, setReflections] = useState<LQReflection[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Load all data
  useEffect(() => {
    setIntentions([]);
    setDuas([]);
    setWorship([]);
    setReflections([]);
    setIsLoading(true);

    const load = async () => {
      if (!user && !isGuest) { setIsLoading(false); return; }

      if (isGuest) {
        try {
          const saved = localStorage.getItem(STORAGE_KEY);
          if (saved) {
            const parsed: LQLocalData = JSON.parse(saved);
            setIntentions(parsed.intentions || []);
            setDuas(parsed.duas || []);
            setWorship(parsed.worship || []);
            setReflections(parsed.reflections || []);
          }
        } catch { /* empty */ }
        setIsLoading(false);
        return;
      }

      try {
        const [intRes, duaRes, worRes, refRes] = await Promise.all([
          supabase.from('lq_intentions').select('*').eq('user_id', user!.id).order('night_number'),
          supabase.from('lq_duas').select('*').eq('user_id', user!.id).order('created_at'),
          supabase.from('lq_worship').select('*').eq('user_id', user!.id).order('night_number'),
          supabase.from('lq_reflections').select('*').eq('user_id', user!.id).order('night_number'),
        ]);

        if (intRes.data) setIntentions(intRes.data as any[]);
        if (duaRes.data) setDuas(duaRes.data as any[]);
        if (worRes.data) setWorship(worRes.data as any[]);
        if (refRes.data) setReflections(refRes.data as any[]);
      } catch (e) {
        console.error('Error loading LQ data:', e);
      }
      setIsLoading(false);
    };

    load();
  }, [user, isGuest]);

  const saveLocal = useCallback((data: Partial<LQLocalData>) => {
    const current = { intentions, duas, worship, reflections, ...data };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(current));
  }, [intentions, duas, worship, reflections]);

  // INTENTIONS
  const saveIntention = useCallback(async (nightNumber: number, text: string) => {
    if (isGuest || !user) {
      const updated = [...intentions.filter(i => i.night_number !== nightNumber), { night_number: nightNumber, intention: text }];
      setIntentions(updated);
      saveLocal({ intentions: updated });
      return;
    }

    try {
      const existing = intentions.find(i => i.night_number === nightNumber);
      if (existing?.id) {
        await supabase.from('lq_intentions').update({ intention: text } as any).eq('id', existing.id);
      } else {
        await supabase.from('lq_intentions').insert({ user_id: user.id, night_number: nightNumber, intention: text } as any);
      }
      const { data } = await supabase.from('lq_intentions').select('*').eq('user_id', user.id).order('night_number');
      if (data) setIntentions(data as any[]);
      toast({ title: 'Intention saved', description: 'May Allah accept your intentions.' });
    } catch (e) {
      console.error('Error saving intention:', e);
      toast({ title: 'Error', description: 'Could not save your intention.', variant: 'destructive' });
    }
  }, [user, isGuest, intentions, saveLocal, toast]);

  // DUAS
  const addDua = useCallback(async (text: string) => {
    if (isGuest || !user) {
      const newDua: LQDua = { dua_text: text, nights_made: [], id: crypto.randomUUID() };
      const updated = [...duas, newDua];
      setDuas(updated);
      saveLocal({ duas: updated });
      return;
    }

    try {
      await supabase.from('lq_duas').insert({ user_id: user.id, dua_text: text, nights_made: [] } as any);
      const { data } = await supabase.from('lq_duas').select('*').eq('user_id', user.id).order('created_at');
      if (data) setDuas(data as any[]);
      toast({ title: 'Dua added', description: 'Your dua has been saved.' });
    } catch (e) {
      console.error('Error adding dua:', e);
    }
  }, [user, isGuest, duas, saveLocal, toast]);

  const markDuaMade = useCallback(async (duaId: string, nightNumber: number) => {
    const dua = duas.find(d => d.id === duaId);
    if (!dua) return;

    const nightsMade = dua.nights_made.includes(nightNumber)
      ? dua.nights_made.filter(n => n !== nightNumber)
      : [...dua.nights_made, nightNumber];

    if (isGuest || !user) {
      const updated = duas.map(d => d.id === duaId ? { ...d, nights_made: nightsMade } : d);
      setDuas(updated);
      saveLocal({ duas: updated });
      return;
    }

    try {
      await supabase.from('lq_duas').update({ nights_made: nightsMade } as any).eq('id', duaId);
      setDuas(prev => prev.map(d => d.id === duaId ? { ...d, nights_made: nightsMade } : d));
    } catch (e) {
      console.error('Error updating dua:', e);
    }
  }, [user, isGuest, duas, saveLocal]);

  const deleteDua = useCallback(async (duaId: string) => {
    if (isGuest || !user) {
      const updated = duas.filter(d => d.id !== duaId);
      setDuas(updated);
      saveLocal({ duas: updated });
      return;
    }

    try {
      await supabase.from('lq_duas').delete().eq('id', duaId);
      setDuas(prev => prev.filter(d => d.id !== duaId));
    } catch (e) {
      console.error('Error deleting dua:', e);
    }
  }, [user, isGuest, duas, saveLocal]);

  // WORSHIP
  const saveWorship = useCallback(async (nightNumber: number, data: Partial<LQWorship>) => {
    const existing = worship.find(w => w.night_number === nightNumber);
    const merged: LQWorship = {
      night_number: nightNumber,
      extra_salah: false,
      quran_recited: false,
      dhikr_done: false,
      charity_given: false,
      dua_made: false,
      ...existing,
      ...data,
    };

    if (isGuest || !user) {
      const updated = [...worship.filter(w => w.night_number !== nightNumber), merged];
      setWorship(updated);
      saveLocal({ worship: updated });
      return;
    }

    try {
      if (existing?.id) {
        await supabase.from('lq_worship').update({
          extra_salah: merged.extra_salah,
          quran_recited: merged.quran_recited,
          dhikr_done: merged.dhikr_done,
          charity_given: merged.charity_given,
          dua_made: merged.dua_made,
        } as any).eq('id', existing.id);
      } else {
        await supabase.from('lq_worship').insert({
          user_id: user.id,
          night_number: nightNumber,
          extra_salah: merged.extra_salah,
          quran_recited: merged.quran_recited,
          dhikr_done: merged.dhikr_done,
          charity_given: merged.charity_given,
          dua_made: merged.dua_made,
        } as any);
      }
      const { data: fresh } = await supabase.from('lq_worship').select('*').eq('user_id', user.id).order('night_number');
      if (fresh) setWorship(fresh as any[]);
    } catch (e) {
      console.error('Error saving worship:', e);
    }
  }, [user, isGuest, worship, saveLocal]);

  // REFLECTIONS
  const saveReflection = useCallback(async (nightNumber: number, text: string, prompt?: string) => {
    if (isGuest || !user) {
      const updated = [...reflections.filter(r => r.night_number !== nightNumber), { night_number: nightNumber, reflection: text, prompt }];
      setReflections(updated);
      saveLocal({ reflections: updated });
      return;
    }

    try {
      const existing = reflections.find(r => r.night_number === nightNumber);
      if (existing?.id) {
        await supabase.from('lq_reflections').update({ reflection: text, prompt } as any).eq('id', existing.id);
      } else {
        await supabase.from('lq_reflections').insert({ user_id: user.id, night_number: nightNumber, reflection: text, prompt } as any);
      }
      const { data } = await supabase.from('lq_reflections').select('*').eq('user_id', user.id).order('night_number');
      if (data) setReflections(data as any[]);
      toast({ title: 'Reflection saved', description: 'May your words be a witness for you.' });
    } catch (e) {
      console.error('Error saving reflection:', e);
      toast({ title: 'Error', description: 'Could not save your reflection.', variant: 'destructive' });
    }
  }, [user, isGuest, reflections, saveLocal, toast]);

  return {
    intentions, duas, worship, reflections, isLoading,
    saveIntention, addDua, markDuaMade, deleteDua, saveWorship, saveReflection,
  };
};
